import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff } from 'lucide-react'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import NoAccess from '@/components/common/NoAccess'
import { PackSection } from '@/components/common/PackSection'
import PageFrame from '@/components/common/PageFrame'
import StatusBadge from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Glass } from '@/components/ui/glass'
import { GlassSelect } from '@/components/ui/glass-select'
import { Textarea } from '@/components/ui/textarea'
import { accessOf, canOpening } from '@/lib/admin-access'
import { backofficeApi, type OpeningDetail } from '@/lib/api-client'
import { isForbidden } from '@/lib/errors'
import { asRecord, dash, fmtDate, fmtTime } from '@/lib/format'
import { humanize } from '@/lib/labels'
import { useAuthStore } from '@/store/auth'

/**
 * Hidden until asked for, like the account number on Client Banks. The API still sends the raw value, so this
 * is a screen guard, not an audited reveal; that needs backoffice-service to mask and log like client banks.
 */
function Masked({ value, what }: { value?: string | null; what: string }) {
  const [shown, setShown] = useState(false)
  if (!value) return dash(value)
  const masked = value.includes('@')
    ? `${value[0]}•••${value.slice(value.indexOf('@'))}`
    : `${'•'.repeat(Math.max(value.length - 4, 2))}${value.slice(-4)}`
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="font-mono">{shown ? value : masked}</span>
      <Button variant="ghost" size="sm" className="size-6 p-0 max-sm:size-11" aria-label={shown ? `Hide ${what}` : `Reveal ${what}`} onClick={() => setShown(!shown)}>
        {shown ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
      </Button>
    </span>
  )
}

function reviewRows(d: OpeningDetail): { identity: [string, unknown][]; address: [string, unknown][]; contact: [string, unknown][]; bank: [string, unknown][]; extra: [string, unknown][] } {
  const c = d.client
  const personal = asRecord(d.draft_data.personal_data)
  const employment = asRecord(d.draft_data.employment)
  const emergency = asRecord(d.draft_data.emergency_contact)
  const prefs = asRecord(d.draft_data.account_preferences)
  const banks = d.banks ?? []
  const firstBank = banks[0]
  return {
    identity: [
      ['Code', c.client_code],
      ['KTP', c.identity_number || personal.identity_number],
      ['Type', c.identity_type],
      ['NPWP', c.npwp_number],
      ['Birth', [c.birth_place, c.birth_date ? fmtDate(c.birth_date) : null].filter(Boolean).join(', ') || personal.birth_place],
      ['Gender', label(c.gender)],
      ['Marital', label(c.marital_status)],
      ['Mother', c.mother_maiden_name],
      ['Nationality', c.nationality_code],
    ],
    contact: [
      ['Email', <Masked key="email" value={c.email} what="email" />],
      ['Mobile', <Masked key="mobile" value={c.mobile_phone} what="mobile number" />],
    ],
    address: [
      ['Street', c.address_street],
      ['Kelurahan', c.address_kelurahan],
      ['Kecamatan', c.address_kecamatan],
      ['Kota', c.address_kota],
      ['Provinsi', c.address_provinsi],
      ['Postal', c.home_postal_code],
      ['Ownership', label(c.home_ownership_status)],
    ],
    bank: firstBank
      ? [
          ['Bank', firstBank.bank_name],
          ['Account', <Masked key="account" value={firstBank.account_number} what="account number" />],
        ]
      : [['Bank', '—']],
    extra: [
      ['Occupation', label(employment.occupation) || employment.company_name],
      ['Company', employment.company_name],
      ['Emergency', emergency.full_name],
      ['Purpose', label(c.account_purpose || prefs.account_purpose)],
    ],
  }
}

/** Intake enums arrive as codes (PEREMPUAN, KELUARGA, GAIN); show them as words. */
function label(value: unknown): unknown {
  return typeof value === 'string' && value ? humanize(value) : value
}

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-lg border border-border bg-card px-5 py-4 ${className ?? ''}`}>{children}</div>
}

const DESCRIPTION = "Compare the applicant's data with the signed CDDS forms. Approving is final."

export function AgreementReviewPage() {
  const { id = '' } = useParams()
  const canDecide = canOpening(accessOf(useAuthStore((s) => s.user)))
  const nav = useNavigate()
  const qc = useQueryClient()
  const [notes, setNotes] = useState('')
  const [risk, setRisk] = useState('')
  const [kyc, setKyc] = useState('')
  const [confirm, setConfirm] = useState<'approve' | 'reject' | 'request-info' | null>(null)
  const q = useQuery({
    queryKey: ['opening-detail', id],
    queryFn: () => backofficeApi.openingDetail(id),
    enabled: Boolean(id),
  })
  const act = useMutation({
    mutationFn: (action: 'approve' | 'reject' | 'request-info') =>
      backofficeApi.openingAction(id, action, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agreements'] })
      void qc.invalidateQueries({ queryKey: ['desk-summary'] })
      nav('/opening/agreements')
    },
  })
  const patch = useMutation({
    mutationFn: () =>
      backofficeApi.openingPatch(id, {
        risk_band: risk || undefined,
        kyc_tier: kyc || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['opening-detail', id] })
      void qc.invalidateQueries({ queryKey: ['agreements'] })
    },
  })
  const d = q.data

  // Fail closed: no form and no actions until the review data has actually loaded.
  if (!d) {
    return (
      <PageFrame title="Review" description={DESCRIPTION}>
        {q.isLoading ? <p className="text-sm text-muted-foreground">Loading the application</p> : null}
        {isForbidden(q.error) ? <NoAccess description="Your role cannot review agreements." /> : null}
        {q.error && !isForbidden(q.error) ? (
          <p role="alert" className="text-sm font-semibold text-destructive">
            {(q.error as Error).message}
          </p>
        ) : null}
      </PageFrame>
    )
  }

  const packs = reviewRows(d)
  // The client needs to know what to fix, so a rejection or a request for information carries notes.
  const notesMissing = notes.trim().length === 0

  return (
    <PageFrame
      title={
        <span className="flex flex-wrap items-center gap-2.5">
          {d.client.full_name || 'Review'}
          {d.client.status ? <StatusBadge status={d.client.status} /> : null}
        </span>
      }
      description={
        <>
          {d.client.client_code ? <span className="font-mono">{d.client.client_code}. </span> : null}
          {DESCRIPTION}
        </>
      }
    >
      {act.error ? <p role="alert" className="text-sm font-semibold text-destructive">{(act.error as Error).message}</p> : null}
      {patch.error ? <p role="alert" className="text-sm font-semibold text-destructive">{(patch.error as Error).message}</p> : null}
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3.5">
          <Card><PackSection title="Identity" rows={packs.identity} mono={['Code', 'KTP', 'NPWP']} /></Card>
          <Card><PackSection title="Contact" rows={packs.contact} /></Card>
          <Card><PackSection title="Address" rows={packs.address} mono={['Postal']} /></Card>
          <div className="flex flex-wrap gap-3.5">
            <Card className="flex-[1_1_300px]"><PackSection title="Bank" rows={packs.bank} /></Card>
            <Card className="flex-[1_1_300px]"><PackSection title="Other" rows={packs.extra} /></Card>
          </div>
        </div>

        <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-3.5 xl:max-w-[440px]">
          <Card>
            <h2 className="mb-2 text-[15px] font-bold">Signed CDDS forms</h2>
            <ul className="max-h-[32rem] overflow-auto text-sm">
              {d.cdds.map((s) => (
                <li key={s.form_code} className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-b-0">
                  <span className="font-mono font-semibold">{s.form_code}</span>
                  <span className="text-muted-foreground">{fmtTime(s.ya_at)}</span>
                </li>
              ))}
              {d.cdds.length === 0 ? <li className="py-2 text-muted-foreground">No signed forms yet.</li> : null}
            </ul>
          </Card>
          <Card>
            <h2 className="mb-3 text-[15px] font-bold">Risk and KYC</h2>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Risk" className="min-w-32 flex-1">
                <GlassSelect
                  value={risk}
                  onChange={setRisk}
                  options={[
                    { value: '', label: 'Not set' },
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                  ]}
                />
              </Field>
              <Field label="KYC" className="min-w-32 flex-1">
                <GlassSelect
                  value={kyc}
                  onChange={setKyc}
                  options={[
                    { value: '', label: 'Not set' },
                    { value: 'simplifikasi', label: 'Simplifikasi' },
                    { value: 'penuh', label: 'Penuh' },
                  ]}
                />
              </Field>
              <Button variant="outline" onClick={() => patch.mutate()} disabled={patch.isPending}>
                Save risk and KYC
              </Button>
            </div>
            {patch.isSuccess ? <p role="status" className="mt-2 text-[13px] text-muted-foreground">Saved.</p> : null}
          </Card>
          <Card>
            <h2 className="mb-3 text-[15px] font-bold">Notes</h2>
            <Field label="Notes for this decision" hint="Required for Reject and Request info. Sent with Request info.">
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </Card>
        </div>
      </div>

      {canDecide ? (
        <div className="pointer-events-none sticky bottom-4 z-10 flex justify-center pt-2">
          <Glass radius={32} role="group" aria-label="Decision" className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 py-2.5 pr-2.5 pl-5">
            <span className="mr-auto truncate pr-2 font-semibold">{d.client.full_name}</span>
            <Button className="h-11 rounded-full px-5" onClick={() => setConfirm('approve')} disabled={act.isPending}>
              Approve
            </Button>
            <Button
              variant="ghost"
              className="h-11 rounded-full px-4"
              onClick={() => setConfirm('request-info')}
              disabled={act.isPending || notesMissing}
              title={notesMissing ? 'Write notes first' : undefined}
            >
              Request info
            </Button>
            <Button
              variant="destructiveSolid"
              className="h-11 rounded-full px-5"
              onClick={() => setConfirm('reject')}
              disabled={act.isPending || notesMissing}
              title={notesMissing ? 'Write notes first' : undefined}
            >
              Reject
            </Button>
          </Glass>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm === 'reject'
            ? 'Reject this agreement?'
            : confirm === 'request-info'
              ? 'Request more information?'
              : 'Approve this agreement?'
        }
        summary={
          confirm === 'request-info'
            ? `${d.client.full_name || 'This client'}: the notes are sent with the request.`
            : `${d.client.full_name || 'This client'}: this decision is final.`
        }
        confirmLabel={confirm === 'reject' ? 'Reject' : confirm === 'request-info' ? 'Request info' : 'Approve'}
        tone={confirm === 'reject' ? 'danger' : 'primary'}
        pending={act.isPending}
        error={act.error ? (act.error as Error).message : null}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm && act.mutate(confirm)}
      />
    </PageFrame>
  )
}
